import { Flex, Text, Tooltip } from '@chakra-ui/react';

import { ColumnDef, createColumnHelper } from '@tanstack/react-table';

import { Badge, BadgeStatus } from '@aslanpay/portal-common-lib';

import { ReconciliationRunStatusValues } from 'app/constants/reconciliations';

import { ReconciliationRunRow, ReconciliationRunStatus } from 'app/types/reconciliations';

import { formatDateTime, formatRunDuration, formatRunScope } from 'app/utils/reconciliations';

/**
 * @description Columns of the reconciliation runs table; clicking a row opens the run.
 */
function getReconciliationColumns(onSelect: (run: ReconciliationRunRow) => void) {
  const columnHelper = createColumnHelper<ReconciliationRunRow>();
  const cellContext = {
    getCellContext: ({ row }: { row: { original: ReconciliationRunRow } }) => ({
      onClick: () => onSelect(row.original),
      cursor: 'pointer',
      'data-testid': `reconciliation-row-${row.original.id}`,
    }),
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnDef<ReconciliationRunRow, any>[] = [
    columnHelper.accessor('status', {
      header: 'Status',
      enableSorting: false,
      cell: (info) => {
        const status = ReconciliationRunStatusValues[info.getValue() as ReconciliationRunStatus];
        return (
          <Badge
            value={status?.value ?? info.getValue()}
            status={(status?.color ?? 'created') as BadgeStatus}
          />
        );
      },
      meta: cellContext,
    }),
    columnHelper.accessor('companyIds', {
      id: 'scope',
      header: 'Scope',
      enableSorting: false,
      cell: (info) => {
        const run = info.row.original;
        const scope = formatRunScope(run.companyIds, run.companyNames);
        return run.companyNames.length > 2 ? (
          <Tooltip label={run.companyNames.join(', ')} placement="top">
            <Text>{scope}</Text>
          </Tooltip>
        ) : (
          scope
        );
      },
      meta: cellContext,
    }),
    columnHelper.accessor('automated', {
      id: 'triggeredBy',
      header: 'Triggered by',
      enableSorting: false,
      cell: (info) =>
        info.getValue() ? 'Nightly run' : info.row.original.triggeredByName || 'An admin',
      meta: cellContext,
    }),
    columnHelper.accessor('queuedAt', {
      header: 'Queued',
      enableSorting: false,
      cell: (info) => formatDateTime(info.getValue()),
      meta: cellContext,
    }),
    columnHelper.accessor('startedAt', {
      header: 'Started',
      enableSorting: false,
      cell: (info) => formatDateTime(info.getValue()),
      meta: cellContext,
    }),
    columnHelper.accessor('completedAt', {
      header: 'Finished',
      enableSorting: false,
      cell: (info) => formatDateTime(info.getValue()),
      meta: cellContext,
    }),
    columnHelper.display({
      id: 'duration',
      header: 'Duration',
      cell: ({ row }) => formatRunDuration(row.original),
      meta: cellContext,
    }),
    columnHelper.display({
      id: 'companies',
      header: 'Companies checked / failed',
      cell: ({ row }) => (
        <Flex gap="4px">
          <Text>{row.original.companiesChecked}</Text>
          <Text color="brand.darkGray">/</Text>
          <Text color={row.original.companiesFailed > 0 ? 'red.500' : undefined}>
            {row.original.companiesFailed}
          </Text>
        </Flex>
      ),
      meta: cellContext,
    }),
    columnHelper.display({
      id: 'problems',
      header: 'Problems / new / resolved',
      cell: ({ row }) =>
        `${row.original.findingsCount} / ${row.original.newFindingsCount} / ${row.original.resolvedFindingsCount}`,
      meta: cellContext,
    }),
  ];

  return columns;
}

export default getReconciliationColumns;
